// Shared constants, types, styles, and helper functions for the Surveillance component.
import { CSSProperties } from 'react';
import { AlertCondition, ConditionGroup } from '../../types';
import { formatInTZ } from '../../utils/timezone';

// ── Constants ──────────────────────────────────────────────────────────────

export const ALL_PROPERTIES = [
    'temperature', 'humidity', 'pressure', 'illuminance_lux', 'occupancy',
    'contact', 'state', 'power', 'energy', 'current', 'voltage', 'battery',
    'io_channels.ch_112', 'io_channels.ch_113', 'io_channels.ch_114',
    'io_channels.ch_115', 'io_channels.ch_116', 'io_channels.ch_117',
];

export const PROPERTY_LABELS: Record<string, string> = {
    temperature: 'Temperatur (°C)', humidity: 'Fukt (%)', pressure: 'Lufttryck (hPa)',
    illuminance_lux: 'Ljusstyrka (lux)', occupancy: 'Rörelse', contact: 'Dörr/fönster',
    state: 'Läge', power: 'Effekt (W)', energy: 'Energi (kWh)',
    current: 'Ström (A)', voltage: 'Spänning (V)', battery: 'Batteri (%)',
    'io_channels.ch_112': 'IO-kanal 112 (in 1)', 'io_channels.ch_113': 'IO-kanal 113 (in 2)',
    'io_channels.ch_114': 'IO-kanal 114 (in 3)', 'io_channels.ch_115': 'IO-kanal 115 (in 4)',
    'io_channels.ch_116': 'IO-kanal 116 (ut 1)', 'io_channels.ch_117': 'IO-kanal 117 (ut 2)',
    online: 'Uppkopplad (1 = ja, 0 = nej)', response_time: 'Svarstid (ms)',
};

export const OPERATORS: { value: string; label: string }[] = [
    { value: '>', label: '> större än' },
    { value: '<', label: '< mindre än' },
    { value: '>=', label: '>= större än eller lika med' },
    { value: '<=', label: '<= mindre än eller lika med' },
    { value: '==', label: '== lika med' },
    { value: '!=', label: '!= inte lika med' },
];

export const CHART_COLORS = [
    '#2196f3', '#e91e63', '#4caf50', '#ff9800', '#9c27b0',
    '#00bcd4', '#f44336', '#8bc34a', '#ff5722', '#3f51b5',
    '#009688', '#ffc107', '#673ab7', '#795548', '#607d8b',
];

// ── Styles ──────────────────────────────────────────────────────────────────

export const sectionStyle: CSSProperties = {
    backgroundColor: 'white', borderRadius: '8px', border: '1px solid #ddd',
    padding: '20px', marginBottom: '24px',
};
export const btnStyle = (color: string, text = 'white'): CSSProperties => ({
    padding: '6px 14px', backgroundColor: color, color: text,
    border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', marginRight: '6px',
});
export const inputStyle: CSSProperties = {
    padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '100%', boxSizing: 'border-box',
};
export const labelStyle: CSSProperties = {
    display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px',
};
export const chipStyle = (active: boolean, color = '#007bff'): CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '4px 10px', borderRadius: '16px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
    border: `1.5px solid ${active ? color : '#ccc'}`,
    backgroundColor: active ? `${color}18` : '#f8f9fa',
    color: active ? color : '#666',
    transition: 'all .15s',
    userSelect: 'none',
});

// ── Shared Types ─────────────────────────────────────────────────────────────

export interface SurveillanceDevice { id: string; name: string; type: string; status: string; disabled?: boolean; }

export interface DataRow {
    id: number; ieee_address: string; name: string;
    property: string;
    value_num: number | null;
    value_str: string | null;
    parameter_name: string | null;
    parameter_unit: string | null;
    enum_values: { value: string; text: string }[] | null;
    timestamp: string;
}

export interface MonitorRow {
    ieee: string; name: string; property: string; propName: string;
    numericValue: number | null; displayStr: string;
    enumValues?: { value: string; text: string }[];
    timestamp: string; unit: string;
}

export interface RuleFormState {
    name: string;
    zone_id: number | null;
    group_name: string;
    condition_groups: ConditionGroup[];
    group_logic: 'AND' | 'OR';
    cooldown_minutes: number;
    trigger_threshold: number;
    trigger_mode: 'count' | 'cooldown';
    contact_ids: number[];
    instruction_text: string;
    instruction_image: string;  // base64 data URI, or '' if none
    imageModified: boolean;     // true only if user explicitly uploaded/removed an image this session
    requires_deviation_report: boolean;
    applies_to_all_devices: boolean;
}

export interface ContactFormState {
    name: string; email: string; phone: string;
    notify_email: boolean; notify_sms: boolean; notify_push: boolean;
    alert_hours_start: string;
    alert_hours_end: string;
    alert_days_mask: number;
    timezone: string;
    defer_offhours: boolean;
}

// ── Property Templates ───────────────────────────────────────────────────────

export interface PropertyTemplate {
    id: number;
    name: string;
    template_type: 'device_type' | 'multi_device';
    device_type: string | null;
    properties: string[];
    devices: { id: string; name: string }[];
}

// ── Helper Functions ─────────────────────────────────────────────────────────

export const formatDate = (s: string, tz?: string) => formatInTZ(s, tz);

export const relativeTime = (now: Date, then: Date): string => {
    const s = Math.round((now.getTime() - then.getTime()) / 1000);
    if (s < 60) return `${s} s sedan`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min sedan`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} tim sedan`;
    return `${Math.round(h / 24)} dygn sedan`;
};

export const toLocalDT = (d: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const makeSeriesKey = (ieee: string, property: string): string =>
    `${ieee}::${encodeURIComponent(property)}`;

export const parseSeriesKey = (seriesKey: string): { ieee: string; property: string } => {
    const idx = seriesKey.indexOf('::');
    if (idx < 0) return { ieee: '', property: seriesKey };
    const ieee = seriesKey.slice(0, idx);
    const encodedProp = seriesKey.slice(idx + 2);
    try {
        return { ieee, property: decodeURIComponent(encodedProp) };
    } catch {
        return { ieee, property: encodedProp };
    }
};

// ── Form Defaults ─────────────────────────────────────────────────────────────

export const blankCondition = (): AlertCondition => ({
    device_ieee: '', property: '', operator: '>', threshold: 0,
});

export const blankRule = (): RuleFormState => ({
    name: '',
    zone_id: null,
    group_name: '',
    condition_groups: [{ logic: 'AND', conditions: [blankCondition()] }],
    group_logic: 'OR',
    cooldown_minutes: 15,
    trigger_threshold: 1,
    trigger_mode: 'count' as 'count' | 'cooldown',
    contact_ids: [],
    instruction_text: '',
    instruction_image: '',
    imageModified: false,
    requires_deviation_report: false,
    applies_to_all_devices: false,
});

export const blankContact = (): ContactFormState => ({
    name: '', email: '', phone: '', notify_email: false, notify_sms: false, notify_push: false,
    alert_hours_start: '', alert_hours_end: '', alert_days_mask: 127, timezone: 'Europe/Stockholm',
    defer_offhours: false,
});
